import React from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { useNavigate } from 'react-router-dom';
import CustomButton from '../CustomButton';
import {
    InvoiceRecipient,
    InvoiceRecipientField,
    useGetInvoiceRecipientQuery,
} from '../../api/invoice/invoiceApiSlice';
import { getCurrentLang } from '../../i18n/i18n';
import { messageOf } from './invoiceMessages';

interface Props {
    orderId: string;
    /** What the confirm button does, e.g. "Details checked, create invoice". */
    confirmTitle: string;
    /** Optional lead sentence above the recipient, e.g. the replacement warning. */
    intro?: string;
    /** Where the booking's customer details are corrected; no link when absent. */
    editBookingUrl?: string;
    busy: boolean;
    onConfirm: () => void;
    onBack: () => void;
    /** Confirmed together with the recipient, above the buttons — the invoice note. */
    children?: React.ReactNode;
}

const countryName = (code: string | null, lang: string): string | null => {
    if (!code) return null;
    try {
        return new Intl.DisplayNames([lang], { type: 'region' }).of(code) ?? code;
    } catch {
        return code;
    }
};

const organizationValue = (
    organization: NonNullable<InvoiceRecipient['organization']>,
    field: InvoiceRecipientField,
    lang: string,
): string | null => {
    switch (field) {
        case 'name': return organization.legalName;
        case 'street': return organization.street;
        case 'postalCode': return organization.postalCode;
        case 'city': return organization.city;
        default: return countryName(organization.country, lang);
    }
};

/**
 * The rows the check shows, in the order the invoice prints them: name, the
 * contact person under a company (#3427), street, postcode and city, country;
 * then the Leitweg-ID and cost centre, shown only when set — an invoice
 * without them is complete.
 */
const recipientRows = (
    data: InvoiceRecipient,
    lang: string,
    t: TFunction,
): Array<[string, string | null | undefined]> => {
    const { buyer } = data;
    const rows: Array<[string, string | null | undefined]> = [
        [t('account.invoice_recipient_name'), buyer.name],
        [t('account.invoice_recipient_street'), buyer.street],
        [t('account.invoice_recipient_postal_city'), [buyer.postalCode, buyer.city].filter(Boolean).join(' ') || null],
        [t('account.invoice_recipient_country'), countryName(buyer.country, lang)],
    ];
    if (buyer.contactPerson) rows.splice(1, 0, [t('account.invoice_recipient_contact_person'), buyer.contactPerson]);
    if (buyer.leitwegId) rows.push([t('account.invoice_recipient_leitweg_id'), buyer.leitwegId]);
    if (data.costCenter) rows.push([t('account.invoice_recipient_cost_center'), data.costCenter]);
    return rows;
};

/**
 * The check before an invoice is issued (PO, 2026-10-12; ADR-0012 amendment
 * 2026-10-12): who the invoice will be addressed to, exactly as issuance will
 * print it, so the admin confirms company name and address before a statutory
 * document freezes them. A wrong value is corrected on the booking — there is
 * no separate invoice address.
 *
 * When the booking is attributed to an organisation whose master data says
 * something else, the differing fields are named; the admin decides which is
 * right.
 *
 * Confirming is impossible while the recipient is loading or could not be
 * read: the point of the step is that someone LOOKED, and a failed read shows
 * nothing to look at.
 */
const InvoiceRecipientCheck = ({
    orderId, confirmTitle, intro, editBookingUrl, busy, onConfirm, onBack, children,
}: Readonly<Props>) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const lang = getCurrentLang();
    const { data, isFetching, isError, error, refetch } = useGetInvoiceRecipientQuery(orderId);

    const missing = t('common.missing');
    const organization = data?.organization ?? null;
    const rows = data ? recipientRows(data, lang, t) : [];

    return (
        <div className={'border border-foreground/10 rounded-[5px] p-4 mt-2'}>
            {intro ? <p className={'text-sm text-foreground mb-3'}>{intro}</p> : null}
            <p className={'text-sm font-medium text-foreground mb-1'}>{t('account.invoice_recipient_title')}</p>
            <p className={'text-xs text-foreground/60 mb-3'}>{t('account.invoice_recipient_hint')}</p>

            {isFetching ? (
                <p className={'text-sm text-foreground/60 mb-3'}>{t('account.invoice_recipient_loading')}</p>
            ) : null}
            {!isFetching && isError ? (
                <div className={'mb-3'}>
                    <p className={'text-sm text-foreground/80 mb-2'}>{t('account.invoice_recipient_failed')}</p>
                    {/* The server's reason, when it gave one: a booking that no
                        longer exists and a store without invoices are not the
                        same problem as a dropped connection. */}
                    {messageOf(error, t) ? (
                        <p className={'text-xs text-foreground/60 mb-2'}>{messageOf(error, t)}</p>
                    ) : null}
                    <CustomButton title={t('account.invoice_recipient_retry')} isFilled={false} onClick={() => refetch()} />
                </div>
            ) : null}

            {!isFetching && !isError && data ? (
                <>
                    <dl className={'grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm mb-3'}>
                        {rows.map(([label, value]) => (
                            <React.Fragment key={label}>
                                <dt className={'text-foreground/60'}>{label}</dt>
                                <dd className={'text-foreground'}>{value || missing}</dd>
                            </React.Fragment>
                        ))}
                    </dl>
                    {organization && data.differences.length > 0 ? (
                        <div className={'border border-foreground/20 rounded-[5px] p-3 mb-3'}>
                            <p className={'text-sm text-foreground mb-1'}>
                                {t('account.invoice_recipient_differs', { organization: organization.legalName })}
                            </p>
                            <ul className={'text-sm text-foreground/80 list-disc pl-5'}>
                                {data.differences.map((field) => (
                                    <li key={field}>
                                        {t(`account.invoice_recipient_field_${field}`)}:{' '}
                                        {organizationValue(organization, field, lang) ?? missing}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : null}
                </>
            ) : null}

            {children}

            <div className={'flex flex-wrap gap-3'}>
                <CustomButton
                    title={confirmTitle}
                    loading={busy}
                    disabled={busy || isFetching || isError || !data}
                    onClick={onConfirm}
                />
                {editBookingUrl ? (
                    <CustomButton
                        title={t('account.invoice_recipient_correct')}
                        isFilled={false}
                        disabled={busy}
                        onClick={() => navigate(editBookingUrl)}
                    />
                ) : null}
                <CustomButton
                    title={t('account.invoice_correction_back')}
                    isFilled={false}
                    disabled={busy}
                    onClick={onBack}
                />
            </div>
        </div>
    );
};

export default InvoiceRecipientCheck;
